import React, { useEffect, useState, useRef } from "react";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  UploadCloud,
  FileSpreadsheet,
  Download,
  Trash2,
  Clock,
} from "lucide-react";
import { API, resolveImage } from "../products";

const TOKEN_KEY = "tcs_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const humanSize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [toast, onDone]);
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div
      data-testid="inv-file-toast"
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white font-semibold ${ok ? "bg-green-600" : "bg-red-600"}`}
    >
      {ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      {toast.msg}
    </div>
  );
}

export default function AdminInventoryFile() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState(null);
  const inputRef = useRef(null);
  const notify = (type, msg) => setToast({ type, msg });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/site/inventory-file`);
      const data = await res.json();
      setFile(data && data.exists ? data : null);
    } catch (e) {
      notify("error", "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await fetch(`${API}/admin/inventory-file`, { method: "POST", headers: authHeaders(), body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "upload");
      }
      const data = await res.json();
      setFile(data);
      notify("ok", "Inventory file published");
    } catch (e) {
      notify("error", typeof e.message === "string" && e.message !== "upload" ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the current inventory file? Visitors won't be able to download it.")) return;
    try {
      const res = await fetch(`${API}/admin/inventory-file`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("delete");
      setFile(null);
      notify("ok", "File removed");
    } catch (e) {
      notify("error", "Remove failed");
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-inventory-file">
      <div>
        <h2 className="font-extrabold text-slate-800 text-lg">Inventory List</h2>
        <p className="text-sm text-slate-500">
          Upload one spreadsheet (Excel, CSV) or PDF. Visitors download it from the homepage and the Inventory page. A new upload replaces the current file.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-14 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {file && (
            <div className="rounded-2xl border border-green-500/40 bg-green-50/50 p-4 flex items-center gap-4" data-testid="inv-file-current">
              <div className="w-12 h-12 rounded-xl bg-green-600/10 flex items-center justify-center shrink-0">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 break-all">{file.filename}</p>
                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  {file.size ? <span>{humanSize(file.size)}</span> : null}
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> live</span>
                </p>
              </div>
              <a
                href={resolveImage(file.url)}
                data-testid="inv-file-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 font-bold text-sm hover:border-green-400"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
              <button
                onClick={remove}
                data-testid="inv-file-remove"
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                title="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.pdf"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="inv-file-input"
          />
          <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
            data-testid="inv-file-dropzone"
            className={`relative cursor-pointer rounded-2xl border-2 border-dashed py-12 px-6 text-center transition-colors ${
              dragging ? "border-green-500 bg-green-50" : "border-slate-300 bg-slate-50 hover:border-green-400"
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center text-green-600"><Loader2 className="w-7 h-7 animate-spin" /></div>
            ) : (
              <>
                <UploadCloud className="w-9 h-9 mx-auto text-slate-400" />
                <p className="mt-2 font-bold text-slate-700">{file ? "Replace file" : "Upload inventory file"}</p>
                <p className="text-xs text-slate-400 mt-1">Drag &amp; drop or click · Excel, CSV or PDF · up to 40MB</p>
              </>
            )}
          </div>
        </>
      )}

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}
